<template>
  <v-container
    class="pa-0"
  >
    <v-dialog
      v-model="deleteDialog"
      persistent
      max-width="290"
    >
      <v-card>
        <v-progress-linear
          v-show="savingData"
          indeterminate
          :color="color"
        />
        <v-card-title
          class="headline"
        >
          Are You Sure?
        </v-card-title>
        <v-card-text>
          Are you sure you wish to delete this item?.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red darken-1"
            text
            @click="confirmDelete()"
          >
            Yes
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="cancelDelete()"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <base-material-card
      :color="color"
      :icon="icon"
      inline
    >
      <template v-slot:after-heading>
        <v-toolbar
          flat
          color="white"
        >
          <v-toolbar-title
            class="display-2 font-weight-light d-none d-sm-flex"
          >
            {{ pluralName }}
          </v-toolbar-title>
          <v-divider
            class="mx-4"
            inset
            vertical
          />
          <v-spacer />
          <v-btn
            v-if="disabled === false"
            :color="color"
            dark
            rounded
            class="mb-2"
            @click.stop="editItem()"
          >
            <v-icon dark>
              mdi-plus
            </v-icon>
            <span class="d-none d-sm-block">New {{ singularName }}</span>
          </v-btn>
          <v-dialog
            :key="editedItem.id"
            v-model="addEditDialog"
            hide-overlay
            persistent
            transition="dialog-bottom-transition"
            max-width="500px"
            :fullscreen="fullscreenEdit"
          >
            <v-toolbar
              v-if="fullscreenEdit"
              dark
              :color="color"
            >
              <v-btn
                icon
                dark
                @click="close"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-toolbar-title
                class="resource-grid-dialog-title"
              >
                <h3 v-text="formTitle" />
                <small v-if="subTitleSource">{{ computeSubTitle() }}</small>
              </v-toolbar-title>
              <v-spacer />
              <v-toolbar-items>
                <v-btn
                  dark
                  text
                  @click="close"
                >
                  Cancel
                </v-btn>
              </v-toolbar-items>
            </v-toolbar>
            <v-card>
              <v-progress-linear
                v-show="savingData"
                indeterminate
                :color="color"
              />
              <v-card-title v-if="!fullscreenEdit">
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>

              <v-card-text
                class="pa-0"
              >
                <v-container
                  :key="editKey"
                >
                  <slot
                    name="editForm"
                    :editedItem="editedItem"
                    :formErrors="formErrors"
                  >
                    Your form should be slotted here.
                  </slot>
                </v-container>
              </v-card-text>

              <slot
                name="actionButtons"
                :editedItem="editedItem"
              >
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    @click="close"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    v-if="disabled === false"
                    color="blue darken-1"
                    text
                    @click="save"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </slot>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <v-row
        class="d-flex d-sm-none"
      >
        <v-col cols="12">
          <v-toolbar-title
            class="display-2 font-weight-light"
          >
            {{ pluralName }}
          </v-toolbar-title>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          md="4"
        >
          <div class="pt-2">
            <slot
              name="gridInteractions"
            />
          </div>
        </v-col>
        <v-col
          cols="12"
          md="4"
          class="text-right"
        >
          <div class="pt-4">
            <v-tooltip
              v-for="(feature, i) in allFeatures"
              :key="i"
              top
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="feature.type === 'button'"
                  :color="feature.color"
                  class="px-1 ml-1"
                  fab
                  x-small
                  v-on="on"
                  @click="handleFeature(feature)"
                >
                  <v-icon v-text="feature.icon" />
                </v-btn>
              </template>
              <span>{{ feature.action }}</span>
            </v-tooltip>
          </div>
        </v-col>
        <v-col
          cols="12"
          md="4"
        >
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            class="ml-auto"
            label="Search"
            hide-details
            single-line
          />
        </v-col>
      </v-row>

      <v-divider class="mt-3" />

      <v-data-table
        :headers="allHeaders"
        :items="items"
        :search.sync="search"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :loading="loading"
        :expanded.sync="expanded"
        :show-expand="allowExpand"
        :items-per-page="-1"
        multi-sort
        loading-text="Loading... Please wait"
        @item-expanded="itemExpanded"
      >
        <template
          v-if="allowExpand"
          v-slot:expanded-item="{ headers, item }"
        >
          <td
            :key="expandKey"
            :colspan="headers.length"
          >
            <slot
              name="rowExpand"
              :details="{item, expandAction}"
            >
              <base-audit-container :item="item" />
            </slot>
          </td>
        </template>
        <template
          v-for="customColumn in customColumns"
          v-slot:[`item.${customColumn}`]="{ item }"
        >
          <slot
            :name="`item.${customColumn}`"
            :item="item"
          >
            {{ item[customColumn] }}
          </slot>
        </template>
        <template v-slot:item.action="{ item }">
          <v-tooltip
            v-for="(action, i) in allActions"
            :key="i"
            top
          >
            <template v-slot:activator="{ on }">
              <v-btn
                :color="action.color"
                class="px-1 ml-1"
                fab
                x-small
                v-on="on"
                @click="handleAction(item, action.action)"
              >
                <v-icon v-text="action.icon" />
              </v-btn>
            </template>
            <span>{{ action.action }}</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <v-btn
            :color="color"
            @click="initialize"
          >
            Reset
          </v-btn>
        </template>
      </v-data-table>
    </base-material-card>
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import { extractFormErrors, getErrorTitle } from '@/services/ErrorHandlers'

  export default {
    name: 'ResourceGrid',

    props: {
      headers: {
        type: Array,
        default: () => ([
          {
            text: 'Unknown Header',
            value: 'unknownHeader',
          },
        ]),
      },
      actions: {
        type: Array,
        default: () => ([]),
      },
      features: {
        type: Array,
        default: () => ([]),
      },
      allowEdit: {
        type: Boolean,
        default: true,
      },
      allowDelete: {
        type: Boolean,
        default: true,
      },
      allowExpand: {
        type: Boolean,
        default: false,
      },
      fullscreenEdit: {
        type: Boolean,
        default: false,
      },
      resource: {
        type: Object,
        default: null,
      },
      staticAssignments: {
        type: Object,
        default: null,
      },
      color: {
        type: String,
        default: 'blue',
      },
      icon: {
        type: String,
        default: 'mdi-account-alert',
      },
      pluralName: {
        type: String,
        default: 'Unknown',
      },
      singularName: {
        type: String,
        default: 'Unknown',
      },
      subTitleSource: {
        type: [String, Function, Array],
        default: null,
      },
      fetchMethod: {
        type: Function,
        default: null,
      },
      prepareSave: {
        type: Function,
        default: null,
      },
      customColumns: {
        type: Array,
      },
      sortBy: {
        type: Array,
        default () {
          return ['name']
        },
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      sortDesc: {
        type: Array,
        default () {
          return [false]
        },
      },
    },

    data: () => ({
      loading: true,
      savingData: false,
      expanded: [],
      items: [],
      search: undefined,
      addEditDialog: false,
      itemToDelete: null,
      editedIndex: -1,
      editedItem: {},
      formErrors: {},
      defaultItem: {},
      errorMessage: null,
      displayError: false,
      expandAction: null,
      expandKey: 0,
      editKey: 1,
    }),

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? `New ${this.singularName}` : `Edit  ${this.singularName}`
      },

      deleteDialog () {
        return this.itemToDelete !== null
      },

      allHeaders () {
        const commonHeaders = [
          {
            sortable: false,
            text: 'Actions',
            value: 'action',
          },
        ]

        return this.headers.concat(commonHeaders)
      },

      allFeatures () {
        const commonFeatures = [
          {
            color: this.color,
            type: 'button',
            icon: 'mdi-refresh',
            action: 'refresh',
          },
        ]

        return this.features.concat(commonFeatures)
      },

      allActions () {
        const allowedActions = []

        if (this.allowEdit) {
          allowedActions.push({
            color: 'success',
            icon: 'mdi-pencil',
            action: 'edit',
          })
        }

        if (this.allowDelete && this.disabled === false) {
          allowedActions.push({
            color: 'error',
            icon: 'mdi-close',
            action: 'delete',
          })
        }

        return this.actions.concat(allowedActions)
      },
    },

    watch: {
      addEditDialog (val) {
        val || this.close()
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      initialize () {
        this.refresh()
      },

      computeSubTitle () {
        let val = null

        // If it's an array, then use items on the editItem
        if (Array.isArray(this.subTitleSource)) {
          const arr = []
          this.subTitleSource.forEach(val => {
            if ((this.editedItem || {})[val]) {
              arr.push((this.editedItem || {})[val])
            }
          })
          if (arr && arr.length) {
            val = arr.join(' - ')
          }
        } else if (typeof val === 'function') {
          val = this.subTitleSource()
        } else {
          val = this.subTitleSource
        }

        return val
      },

      isCustomColumn (value) {
        let isCustom = false
        if (this.customColumns) {
          isCustom = this.customColumns.contains(value)
        }
        return isCustom
      },

      refresh () {
        this.loading = true

        let dataPromise = null
        if (this.fetchMethod) {
          dataPromise = this.fetchMethod()
        } else {
          dataPromise = this.resource.getList()
        }

        dataPromise
          .then(data => {
            this.loading = false
            this.items = data
          })
          .catch((error) => {
            this.showError(error, 'Failed to load!')
            this.loading = false
          })
      },

      showError (error, msg) {
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },

      handleFeature (feature) {
        if (feature.action === 'refresh') {
          this.refresh()
        }
      },

      handleAction (item, action) {
        if (action === 'edit') {
          this.editItem(item)
        } else if (action === 'delete') {
          this.deleteItem(item)
        }
        this.$emit('handleAction', action, item, this.expanded)
      },

      infoItem (item) {
        alert(JSON.stringify(item))
      },

      editItem (item) {
        this.editKey++
        if (item) {
          this.editedIndex = this.items.indexOf(item)
          this.editedItem = Object.assign({}, item)
        }
        this.formErrors = {}
        this.addEditDialog = true
      },

      deleteItem (item) {
        this.itemToDelete = item
      },

      confirmDelete () {
        this.savingData = true
        this.resource.delete(this.itemToDelete.id)
          .then((data) => {
            this.savingData = false
            const index = this.items.indexOf(this.itemToDelete)
            this.items.splice(index, 1)
            this.itemToDelete = null
            this.$emit('item:deleted', this.itemToDelete)
          })
          .catch((error) => {
            this.savingData = false
            this.showError(error, 'Failed to delete!')
            this.itemToDelete = null
          })
      },

      cancelDelete () {
        this.itemToDelete = null
      },

      close () {
        this.addEditDialog = false
        this.formErrors = {}
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
          this.editKey++
        }, 300)
      },

      itemExpanded (data) {
        this.$emit('item-expanded', data)
      },

      expand (expandAction, record) {
        this.expanded = [record]
        this.expandAction = expandAction
        this.expandKey++
      },

      collapse () {
        this.expanded = []
      },

      itemEdited (data) {
        let newItem = false

        if (this.editedIndex > -1) {
          Object.assign(this.items[this.editedIndex], data)
          this.$emit('item:updated', data)
        } else {
          newItem = true
          this.items.push(data)
          this.$emit('item:added', data)
        }

        return newItem
      },

      save () {
        this.savingData = true
        let payload = Object.assign(this.editedItem, (this.staticAssignments || {}))
        if (this.prepareSave) {
          payload = this.prepareSave(payload)
        }
        this.resource.save(payload)
          .then((data) => {
            this.savingData = false
            const newItem = this.itemEdited(data)
            this.$emit('saved', newItem, data)
            this.close()
          })
          .catch((error) => {
            this.savingData = false
            this.formErrors = extractFormErrors(error)
            if (this.formErrors) {
              this.showError(getErrorTitle(error), 'Failed to save!')
            } else {
              this.formErrors = {}
              this.showError(error, 'Failed to save!')
            }
          })
      },
    },
  }
</script>

<style lang="sass">
  .resource-grid-dialog-title
    line-height: 1
</style>
